import { Provider } from '@/features/game-context/types';
import {
  sendStopAutoplay,
  toggleHowToPlay,
  toggleMute,
  togglePaytable,
  updateBalance,
} from '@/features/game-window-integration/helpers/GCM/actions';
import {
  dialogAcknowledged,
  gameAnimationComplete,
  gameAnimationStart,
  gameReady,
  handleError,
  handleRealityCheckErrorCallback,
  handleRecoverableErrorCallback,
  loadProgressUpdate,
  onGcmInit,
  optionHasChanged,
  redirect,
  reload,
  returnToLobby,
} from '@/features/game-window-integration/helpers/GCM/handlers';
import {
  loadScript,
  log,
  resolveUrl,
  setOGSParams,
} from '@/features/game-window-integration/helpers/GCM/helpers';
import { gcmState } from '@/features/game-window-integration/helpers/GCM/state';
import { GcmObject } from '@/features/game-window-integration/helpers/GCM/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy.ts';

let gcmObject: GcmObject = {
  onGcmInit,
  handleError,
  setOGSParams,
  reload,
  redirect,
  returnToLobby,
  loadProgressUpdate,
  handleRealityCheckErrorCallback,
  handleRecoverableErrorCallback,
  gameAnimationComplete,
  gameAnimationStart,
  gameReady,
  optionHasChanged,
  toggleMute,
  togglePaytable,
  toggleHowToPlay,
  sendStopAutoplay,
  updateBalance,
  dialogAcknowledged,
  log,
};

export const init = async (
  region: string,
  gameUid?: string,
): Promise<GcmObject | void> => {
  try {
    const gcmLibUrl = resolveUrl(region);

    if (!gcmLibUrl) {
      throw new Error('GCM URL could not be resolved');
    }

    await loadScript(gcmLibUrl);

    const aviatorConditionMet =
      gameUid === 'aviator' && process.env?.COUNTRY === 'canada';

    const gameAnimationCompleteHandler = (): void => {
      if (!gcmState.gcm) return;
      gcmState.gcm.resume();
      if (aviatorConditionMet) {
        setTimeout(() => {
          WrapperProxy.setSpinInProgress(false);
        }, 1000);
      } else {
        WrapperProxy.setSpinInProgress(false);
      }
    };

    gcmObject = {
      ...gcmObject,
      gameAnimationComplete: gameAnimationCompleteHandler,
    };

    const gcmConfig = {
      cui: gcmObject,
      errorHandler: (err: { message: string }) => {
        if (!err.message.includes('Invalid post msg')) {
          console.log('>> GCM Error: ', { err });
        }
      },
    };

    const { sgdigital } = window;
    if (!sgdigital) {
      throw new Error('GCM is not available after load');
    }

    gcmState.gcm = new sgdigital.casino.GcmProxy();
    gcmState.gcm?.init(gcmConfig);
    gcmState.initComplete = true;

    return gcmObject;
  } catch (err) {
    Logger.logRumError({
      name: '[GCM] Initialization Failure',
      useCase: DdEventCategory.ProviderIntegration,
      eventType: DdEventType.Error,
      context: {
        error: err,
        region,
        provider: Provider.LNW,
      },
    });
    return undefined;
  }
};
